import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Loader2, MessageCircle, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { aptWaUrl } from '../../../lib/aptMessages';
import { formatErr, inputCls } from '../billing/shared';

// Move a booking to a new slot, then offer the WhatsApp notice as a real link (works on iOS).
export const RescheduleDialog = ({ row, authHeader, onClose, onDone }) => {
  const [date, setDate] = useState(row.slot_date);
  const [win, setWin] = useState('');
  const [slots, setSlots] = useState(null);
  const [closed, setClosed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  useEffect(() => {
    setWin(''); setSlots(null);
    axios.get(`${API}/public/appointments/slots`, { params: { date, service_type: row.service_type } })
      .then(({ data }) => { setSlots(data.slots || []); setClosed(!!data.closed); }).catch(() => setSlots([]));
  }, [date, row.service_type]);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const { data } = await axios.patch(`${API}/appointments/${row.id}`, { slot_date: date, slot_window: win }, { headers: authHeader() });
      setSaved(data); onDone();
    } catch (e) { setErr(formatErr(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-testid="apt-reschedule-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><CalendarClock size={18} className="text-teal-700" /> Reschedule {row.ref_no}</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="apt-reschedule-close"><X size={18} className="text-slate-500" /></button>
        </div>
        {saved ? (
          <div className="space-y-3" data-testid="apt-reschedule-done">
            <p className="text-sm text-slate-700">Moved to <b>{saved.slot_date} · {saved.slot_window}</b>. Let the customer know:</p>
            <a href={aptWaUrl(saved, 'appointment_reschedule')} target="_blank" rel="noopener noreferrer" onClick={() => setTimeout(onClose, 300)} data-testid="apt-reschedule-wa"
              className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold" style={{ color: '#fff' }}>
              <MessageCircle size={15} /> Send new time on WhatsApp
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            <input type="date" className={inputCls} value={date} min={new Date().toLocaleDateString('en-CA')} onChange={(e) => setDate(e.target.value)} data-testid="apt-reschedule-date" />
            {slots === null && <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="apt-reschedule-loading"><Loader2 size={13} className="animate-spin" /> Checking free slots…</p>}
            {closed && <p className="text-xs font-semibold text-slate-600" data-testid="apt-reschedule-closed">The workshop is closed on this day. Pick another day.</p>}
            <div className="grid grid-cols-2 gap-2">
              {!closed && (slots || []).map((s) => {
                const full = s.remaining <= 0 && !(date === row.slot_date && s.window === row.slot_window);
                return (
                  <button key={s.window} type="button" disabled={full} onClick={() => setWin(s.window)} data-testid={`apt-reschedule-slot-${s.window}`}
                    className={`rounded-lg border px-3 py-2 text-left text-xs font-semibold ${win === s.window ? 'border-teal-600 bg-teal-600 text-white' : full ? 'border-slate-200 bg-slate-50 text-slate-400' : 'border-slate-300 hover:border-teal-400'}`}>
                    {s.window}<span className="block text-[10px] font-normal opacity-80">{full ? (s.unavailable_reason || 'Full') : `${s.remaining} left`}</span>
                  </button>
                );
              })}
            </div>
            {err && <p className="text-xs text-rose-700" data-testid="apt-reschedule-err">{err}</p>}
            <button type="button" disabled={!win || saving} onClick={save} data-testid="apt-reschedule-save"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 py-2.5 text-sm font-semibold text-white disabled:opacity-40">
              {saving && <Loader2 size={14} className="animate-spin" />} Move booking
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
